import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { Flex } from '@atlassian/eoc-flex';

import messages from './messages';
import { type RuleCardActionsProps, type RuleCardActionsRefProps } from './types';

export const RuleCardActions = forwardRef<RuleCardActionsRefProps, RuleCardActionsProps>(
	(
		{
			isLastItem,
			isLoading,
			onEdit,
			onClone,
			onDelete,
			name,
			hasEditRight,
			hasDeleteRight,
			isDeleted,
		},
		ref,
	) => {
		const { formatMessage } = useIntl();

		const buttonRef = useRef<HTMLButtonElement>(null);

		useImperativeHandle(ref, () => ({
			getActionButtonRef: () => buttonRef,
		}));

		if (isLastItem) {
			return null;
		}

		return (
			<Flex alignItems="center" columnGap={1.5}>
				{isLoading && <Spinner testId="routing-rules.rule-card-actions.deleting" size="small" />}
				{isDeleted && (
					<Lozenge appearance="removed">{formatMessage(messages.routeTargetDeleted)}</Lozenge>
				)}
				{(hasEditRight || hasDeleteRight) && (
					<DropdownMenu<HTMLButtonElement>
						trigger={({ triggerRef, ...props }) => (
							<IconButton
								{...props}
								label={formatMessage(messages.showMore, { name })}
								ref={mergeRefs([triggerRef, buttonRef])}
								appearance="subtle"
								icon={MoreIcon}
							/>
						)}
						shouldRenderToParent
					>
						<DropdownItemGroup>
							{hasEditRight && (
								<DropdownItem onClick={onEdit}>{formatMessage(messages.edit)}</DropdownItem>
							)}
							{hasEditRight && (
								<DropdownItem onClick={onClone}>{formatMessage(messages.clone)}</DropdownItem>
							)}
							{hasDeleteRight && (
								<DropdownItem onClick={onDelete}>{formatMessage(messages.delete)}</DropdownItem>
							)}
						</DropdownItemGroup>
					</DropdownMenu>
				)}
			</Flex>
		);
	},
);
